import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const withRefresh = async (fn) => {
  try { return await fn(); } catch (e) {
    if (e?.response?.status !== 401) throw e;
    await http.post("/auth/refresh");
    return fn();
  }
};

const Login = ({ onDone }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { const { data } = await http.post("/auth/login", f); onDone(data); }
    catch (x) { setErr(errText(x)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-mist px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-white p-8 shadow-[0_30px_60px_-30px_rgba(10,26,51,0.35)]" data-testid="admin-login-form">
        <Logo />
        <h1 className="mt-8 font-display text-2xl font-extrabold text-navy">Lead inbox</h1>
        <p className="mt-1 text-sm text-slate-600">Admin sign-in</p>
        <input className="mt-6 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm focus:border-gold focus:outline-none" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email-input" required />
        <input className="mt-3 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm focus:border-gold focus:outline-none" type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password-input" required />
        {err && <p className="mt-3 text-sm text-rose-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} className="mt-6 w-full rounded-full bg-navy py-3 text-sm font-semibold text-white transition-colors hover:bg-gold disabled:opacity-50" data-testid="admin-login-button">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </div>
  );
};

const STATUS = ["new", "contacted", "closed"];
const BADGE = { new: "bg-gold/15 text-gold", contacted: "bg-sky-100 text-sky-800", closed: "bg-slate-100 text-slate-600" };

const LeadRow = ({ l, onStatus }) => (
  <tr className="border-t border-navy/10 align-top" data-testid={`lead-row-${l.id}`}>
    <td className="py-4 pr-4 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
    <td className="py-4 pr-4"><p className="font-semibold text-navy">{l.name}</p><p className="text-xs text-slate-500">{l.company || "—"}</p></td>
    <td className="py-4 pr-4 text-sm"><a className="text-navy underline" href={`mailto:${l.email}`}>{l.email}</a><p className="text-xs text-slate-500">{l.phone || ""}</p></td>
    <td className="py-4 pr-4 text-sm capitalize text-slate-700">{l.role}</td>
    <td className="py-4 pr-4 text-sm"><p className="font-semibold text-navy">{l.material}</p><p className="text-xs text-slate-500">{[l.grade, l.quantity, l.port].filter(Boolean).join(" · ") || "—"}</p></td>
    <td className="max-w-[260px] py-4 pr-4 text-xs text-slate-600">{l.message || "—"}</td>
    <td className="py-4">
      <select value={l.status} onChange={(e) => onStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className={`rounded-full border-0 px-3 py-1 text-xs font-semibold capitalize ${BADGE[l.status]}`}>
        {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </td>
  </tr>
);

const Inboxes = ({ user, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await withRefresh(() => http.get("/leads")); setLeads(Array.isArray(data) ? data : []); }
    catch (e) { if (e?.response?.status === 401) onLogout(); else toast.error(errText(e)); }
    finally { setLoading(false); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try { await withRefresh(() => http.patch(`/leads/${id}`, { status })); setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l))); }
    catch (e) { toast.error(errText(e)); }
  };
  const fresh = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen bg-mist">
      <header className="border-b border-navy/10 bg-white">
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <a href="/" data-testid="admin-home-link"><Logo /></a>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-600 sm:inline">{user.email}</span>
            <button onClick={onLogout} className="flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy hover:border-navy" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Sign out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-navy sm:text-4xl">Quote requests</h1>
            <p className="mt-1 text-sm text-slate-600" data-testid="admin-lead-count">{leads.length} total · {fresh} new</p>
          </div>
          <button onClick={load} className="flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-gold" data-testid="admin-refresh-button"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</button>
        </div>
        <div className="mt-8 overflow-x-auto rounded-3xl bg-white p-6">
          {!loading && leads.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-slate-500" data-testid="admin-empty"><Inbox className="h-10 w-10" /><p className="mt-3">No quote requests yet.</p></div>
          ) : (
            <table className="w-full min-w-[960px] text-left" data-testid="admin-leads-table">
              <thead><tr className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                <th className="pb-3">Received</th><th className="pb-3">Contact</th><th className="pb-3">Email / phone</th><th className="pb-3">Role</th><th className="pb-3">Material</th><th className="pb-3">Message</th><th className="pb-3">Status</th>
              </tr></thead>
              <tbody>{leads.map((l) => <LeadRow key={l.id} l={l} onStatus={onStatus} />)}</tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    withRefresh(() => http.get("/auth/me")).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = useCallback(async () => { await http.post("/auth/logout").catch(() => {}); setUser(false); }, []);
  if (user === null) return <div className="grid min-h-screen place-items-center bg-mist text-sm text-slate-500">Loading…</div>;
  if (!user) return <Login onDone={setUser} />;
  return <Inboxes user={user} onLogout={logout} />;
}
